import get from "lodash.get";
import pick from "lodash.pick";
import jwt_decode from "jwt-decode";
import { format, addDays, compareAsc, getTime, parse, toDate } from "date-fns";
import { LoggerService as logger, WorkspacesService } from "vue-components-library";
import { App } from "vue-components-library/dist/src/services/Workspaces";

import router from "@/App/router";
import { IUser, LoginResponse } from "@/Login/services";
import { AuthorizationToken } from "@/Login/services";
import type { EMSAgency } from "@/types/EMSAgency";
import { ApiPrefetcher } from "@/App/services/ApiPrefetcher";
import { LoginRequestBody, AuthorizationService } from "@/Login/services";
import { PLEASE_FILL_PASSWORD, PLEASE_FILL_USERNAME } from "@/Login/store/Messages";
import { runAutoLogoutCheckService } from "@/Shift/services/shift.service";
import { TeamSelectionService } from "@/TeamSelection/services/TeamSelection.Service";
import { reloadPage } from "@/App/utils/reloadPage";
import { Storage as storage, isStorageUnencrypted, clearStorage } from "@/App/services/LocalStorage";

const LOG_TAG = "[LOGIN STORE]";

const state = getDefaultState();

function getDefaultState() {
  return {
    errorMessage: "",
    usernameError: "",
    passwordError: "",
    isLoading: false,
    auth: {
      token: storage.getItem("jwt") || "",
      expirationDate: storage.getItem("jwtExpirationDate") || "",
    },
    user: JSON.parse(storage.getItem("user") || "null"),
    teams: JSON.parse(storage.getItem("teams") || "[]"),
    autoLogoutTime: null,
    workspace: {
      app: undefined,
    },
  };
}

const getters = {
  authToken: (state: any) => state.auth.token,
  authExpiration: (state: any) => state.auth.expirationDate,
  loggedIn: (state: any) => state.auth.token && new Date() < new Date(state.auth.expirationDate),
  user: (state: any) => state.user,
  userId: (state: any) => state.user?._id,
  teams: (state: any) => state.teams,
  autoLogoutTime: (state: any) => state.autoLogoutTime && format(toDate(state.autoLogoutTime), "hh:mm a"),
  gpsConfig: (state: any) => ({
    source: "port",
    portType: "udp4",
    port: 21000,
    host: "0.0.0.0",
    dataFormat: "TAIP",
  }),
  workspaceApp: (state: any) => state.workspace.app,
};

const actions = {
  async authenticate({ commit, dispatch }: any, data: LoginRequestBody) {
    if (!data.username || !data.password) {
      if (!data.username) {
        commit("SET_USERNAME_ERROR", PLEASE_FILL_USERNAME);
      }

      if (!data.password) {
        commit("SET_PASSWORD_ERROR", PLEASE_FILL_PASSWORD);
      }

      return;
    }

    try {
      const { authorizationToken, user } = await AuthorizationService.login(data);

      await dispatch("enforceCaseOwnership", { authorizationToken, user });

      commit("SET_AUTH", authorizationToken);
      commit("SET_USER", user);

      dispatch("Authenticate/setAuthenticate", false, {
        root: true,
      });

      reloadPage();
    } catch (error) {
      dispatch("setGeneralError", error);
    }
  },

  async login({ commit, dispatch }: any, data: LoginRequestBody) {
    if (!data.username || !data.password) {
      if (!data.username) {
        commit("SET_USERNAME_ERROR", PLEASE_FILL_USERNAME);
      }
      if (!data.password) {
        commit("SET_PASSWORD_ERROR", PLEASE_FILL_PASSWORD);
      }
      return;
    }

    commit("SET_LOADING", true);

    try {
      const loginResponse = await AuthorizationService.login(data);

      await dispatch("setupUser", loginResponse);

      await router.push({ name: "team-selection" });
    } catch (error) {
      dispatch("setGeneralError", error, pick(data, ["username"]));
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async setupUser({ commit, dispatch }, loginResponse: LoginResponse) {
    const { authorizationToken, user } = loginResponse;

    try {
      await dispatch("enforceCaseOwnership", loginResponse);

      commit("SET_AUTH", authorizationToken);
      commit("SET_USER", user);

      logger.addUserToDatadogContext(user);
      logger.info(`${LOG_TAG} User logged in`, { at: new Date().toISOString() });

      const emsAgencyIDs: string[] = (user.emsAgencyMemberships ?? [])
        .concat(user.emsAgenciesAdministered || [])
        .map(({ _id }) => _id);

      const uniqueAgencyIds = [...new Set(emsAgencyIDs)];

      const emsAgenciesPromise = uniqueAgencyIds.map((emsAgencyID) =>
        TeamSelectionService.getTeam(String(emsAgencyID)),
      );

      const emsAgencies = await Promise.all(emsAgenciesPromise);

      commit("setEMSAgencies", emsAgencies);

      dispatch("PubNubSubscription/loadPubNub", null, { root: true });

      if ("Notification" in window) {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          dispatch("PushNotification/requestToken", undefined, {
            root: true,
          });
        }
      }

      dispatch("prefetchApiResources");
    } catch (error) {
      logger.error(`${LOG_TAG} Failed to setup User state`, { error });
      logger.removeUserFromDatadogContext();
      dispatch("logout");
      throw error;
    }

    if (window.electron) {
      dispatch("GpsSource/resetState", null, { root: true });
    }
  },

  async logout({ state, dispatch, getters }: any) {
    const { workspaceApp } = getters;

    try {
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          action: "clearRuntimeCache",
        });
      }

      dispatch("fullyResetState");

      logger.info(`${LOG_TAG} User logged out`, { at: new Date().toISOString() });

      const cognitoSignout = get(workspaceApp, "cognito.signout");
      if (cognitoSignout) {
        window.location.href = cognitoSignout;
        return;
      } else {
        router.push("/");
      }

      if (state.user?.id && actions.isTokenValid({ state })) {
        dispatch("PushNotification/unsubscribe", `user-${state.user.id}`, {
          root: true,
        });
        await AuthorizationService.logout();
      }
    } catch (error) {
      dispatch("setGeneralError", error);
    }
  },

  fullyResetState({ commit, dispatch }) {
    clearStorage();

    commit("RESET_STATE");

    dispatch("TruckSelection/resetState", null, { root: true });
    dispatch("TeamSelection/resetState", null, { root: true });
    dispatch("Case/resetState", null, { root: true });
    dispatch("PubNubSubscription/resetState", null, { root: true });
    dispatch("PushNotification/resetState", null, { root: true });
    dispatch("ChangePassword/resetState", null, { root: true });

    logger.removeUserFromDatadogContext();
    isStorageUnencrypted();
  },

  setGeneralError({ commit }: any, error: any, logPayload: any = undefined) {
    commit("SET_GENERAL_ERROR", error);
    logger.error(`${LOG_TAG} ${error}`, logPayload);
  },
  prefetchApiResources({ commit, state }: any) {
    Promise.all([ApiPrefetcher.fetchTrucks(state.teams), ApiPrefetcher.fetchComplaints(state.teams)]);
  },
  setAutoLogout({ commit, dispatch }, time) {
    if (time === null) {
      commit("unsetAutoLogoutTime");

      return;
    }

    let autoLogoutTime = parse(time, "HH:mm:ss", new Date());

    const now = toDate(Date.now());

    const x = compareAsc(autoLogoutTime, now);

    if (x !== 1) {
      autoLogoutTime = addDays(autoLogoutTime, 1);
    }

    const autoLogoutTimeInMilliseconds = getTime(autoLogoutTime);

    commit("setAutoLogoutTime", autoLogoutTimeInMilliseconds);

    dispatch("startAutoLogoutCheck");
  },
  startAutoLogoutCheck({ commit, dispatch }) {
    const autoLogoutTime = Number(storage.getItem("autoLogoutTime"));

    if (autoLogoutTime) {
      commit("setAutoLogoutTime", autoLogoutTime);

      runAutoLogoutCheckService({ dispatch });
    }
  },
  isTokenValid({ state }) {
    let isValid = false;

    if (state.auth.token) {
      try {
        const jwtDecoded: any = jwt_decode(state.auth.token);
        const jwtExpirationDate = new Date(jwtDecoded.exp * 1000);
        if (jwtExpirationDate > new Date()) {
          isValid = true;
        }
      } catch (error) {
        logger.error(`${LOG_TAG} Failed to parse jwt token`, { error });
      }
    }

    return isValid;
  },
  validateToken({ state, dispatch, rootGetters }) {
    if (state.auth.token && !actions.isTokenValid({ state })) {
      const isCaseActive = rootGetters["Case/isCaseActive"];

      if (isCaseActive) {
        dispatch("Authenticate/setAuthenticate", true, {
          root: true,
        });
      } else {
        dispatch("logout");
      }
    }
  },
  async setupWorkspaceApp({ commit }) {
    const appName = "ems-web";
    const subdomain = WorkspacesService.findSubdomain();

    const app = await WorkspacesService.app({
      appName,
      subdomain,
    });

    logger.info(`${LOG_TAG} Workspace Application setup.`, { app, appName, subdomain });

    if (!app) {
      return;
    }

    commit("SET_WORKSPACE_APP", app);
  },
  async enforceCaseOwnership({ dispatch, getters, rootGetters }, loginResponse) {
    const { user } = getters;
    const { user: incomingUser } = loginResponse;

    const caseOwnerChanged = rootGetters["Case/isCaseActive"] && user && user.id !== incomingUser.id;

    if (caseOwnerChanged) {
      await dispatch("fullyResetState");
    }
  },
};

const mutations = {
  SET_GENERAL_ERROR(state: any, message: string) {
    state.errorMessage = message;
  },
  SET_USERNAME_ERROR(state: any, message: string) {
    state.usernameError = message;
  },
  SET_PASSWORD_ERROR(state: any, message: string) {
    state.passwordError = message;
  },
  SET_LOADING(state: any, message: string) {
    state.isLoading = message;
  },
  CLEAR_ERRORS(state: any) {
    state.errorMessage = "";
    state.usernameError = "";
    state.passwordError = "";
  },
  SET_AUTH(state: any, authToken: AuthorizationToken) {
    state.auth = authToken;
    storage.setItem("jwtExpirationDate", authToken.expirationDate);
  },
  SET_USER(state: any, user: IUser) {
    state.user = user;
  },
  RESET_STATE(state: any) {
    const initialState: any = getDefaultState();

    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
  SET_WORKSPACE_APP(state: any, workspaceApp: App) {
    state.workspace.app = workspaceApp;
  },

  setAutoLogoutTime(state, autoLogoutTime) {
    state.autoLogoutTime = autoLogoutTime;

    storage.setItem("autoLogoutTime", autoLogoutTime);
  },

  setEMSAgencies(state, emsAgencies: EMSAgency[]) {
    state.teams = emsAgencies;

    storage.setItem("teams", JSON.stringify(emsAgencies));
  },

  unsetAutoLogoutTime() {
    storage.removeItem("autoLogoutTime");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
